import React, { useState, useRef } from "react";
import html2canvas from "html2canvas";

const SONG_FIELDS = [
  { key: "entrance", label: "Entrance Song(s)" },
  { key: "kyrie", label: "Kyrie" },
  { key: "gloria", label: "Gloria" },
  { key: "responsorial_psalm", label: "Responsorial Psalm" },
  { key: "gospel_acclamation", label: "Gospel Acclamation" },
  { key: "credo", label: "Credo" },
  { key: "response_to_prayers", label: "Response to Prayers" },
  { key: "pre_consecration", label: "Pre-Consecration" },
  { key: "offertory", label: "Offertory Song(s)" },
  { key: "sanctus", label: "Sanctus" },
  { key: "agnus_dei", label: "Agnus Dei" },
  { key: "communion", label: "Communion Song(s)" },
  { key: "dismissal", label: "Dismissal Song(s)" }
];

const INITIAL_FORM = {
  choir_name: "",
  church_name: "",
  occasion: "",
  date: "",
  conductor: "",
  organist: "",
  psalmist: "",
  instrumentalists: "",
  ...Object.fromEntries(SONG_FIELDS.map(f => [f.key, [""]]))
};

export default function SelectionsManager() {
  const [form, setForm] = useState(INITIAL_FORM);
  const [showResult, setShowResult] = useState(false);
  const [imgUrl, setImgUrl] = useState(null);
  const [generating, setGenerating] = useState(false);
  const resultRef = useRef();

  // Form logic
  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const handleSongChange = (key, idx, value) => {
    const updated = [...form[key]];
    updated[idx] = value;
    setForm({ ...form, [key]: updated });
  };
  const addSongRow = (key) => setForm({ ...form, [key]: [...form[key], ""] });
  const removeSongRow = (key, idx) => {
    const updated = form[key].filter((_, i) => i !== idx);
    setForm({ ...form, [key]: updated.length ? updated : [""] });
  };

  const handleGenerate = (e) => {
    e.preventDefault();
    setShowResult(true);
    setImgUrl(null);
    setGenerating(false);
  };

  // Save/Share as Image logic
  const handleSaveShare = async () => {
    setGenerating(true);
    setImgUrl(null);
    setTimeout(async () => {
      if (resultRef.current) {
        const node = resultRef.current;
        const width = node.scrollWidth;
        const height = node.scrollHeight;
        try {
          const canvas = await html2canvas(node, {
            backgroundColor: "#f9fafb",
            scale: 2,
            width,
            height,
            useCORS: true
          });
          const url = canvas.toDataURL("image/png");
          setImgUrl(url);
        } catch (err) {
          alert("Sorry, unable to generate image. Try again or check your browser.");
        }
        setGenerating(false);
      }
    }, 100);
  };

  const handleNew = () => {
    setForm(INITIAL_FORM);
    setShowResult(false);
    setImgUrl(null);
    setGenerating(false);
  };

  // Social sharing
  const shareOnWhatsApp = () => {
    if (!imgUrl) return;
    const shareUrl = window.location.href;
    const text = encodeURIComponent("See this choir music selection from ChoirCenter:\n" + shareUrl);
    window.open(`https://wa.me/?text=${text}`, "_blank");
  };
  const shareOnFacebook = () => {
    if (!imgUrl) return;
    const shareUrl = window.location.href;
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, "_blank");
  };

  const downloadImage = () => {
    if (imgUrl) {
      const link = document.createElement("a");
      link.href = imgUrl;
      link.download = "choir_selection.png";
      link.click();
    }
  };

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-green-100 via-green-50 to-green-200 py-10 px-2 flex flex-col items-center font-sans">
      <div className="w-full max-w-2xl mx-auto flex flex-col items-center">
        {/* HEADER */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-3">
            <div className="rounded-full bg-green-100 shadow-lg p-4 border border-green-200">
              <svg width="38" height="38" fill="none" viewBox="0 0 24 24" stroke="#15803d" className="w-8 h-8">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 18V5.25M15 18V5.25M9 5.25l-2.25 2.25M15 5.25l2.25 2.25" />
              </svg>
            </div>
          </div>
          <h1 className="text-3xl md:text-4xl font-black text-green-900 mb-1 tracking-tight">Selections Manager</h1>
          <p className="text-base md:text-lg text-green-800 font-medium max-w-xl mx-auto">
            Make, organise and share your choir’s song selections for Mass.
          </p>
        </div>

        {/* FORM */}
        {!showResult && (
          <form
            className="w-full bg-white shadow-xl rounded-3xl p-6 mb-8 border border-green-100 animate-fade-in grid gap-6"
            onSubmit={handleGenerate}
            autoComplete="off"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="label-green">Choir Name</label>
                <input
                  name="choir_name"
                  value={form.choir_name}
                  onChange={handleChange}
                  placeholder="e.g. St. Cecilia Choir"
                  className="input-green"
                  autoFocus
                />
              </div>
              <div>
                <label className="label-green">Church Name</label>
                <input
                  name="church_name"
                  value={form.church_name}
                  onChange={handleChange}
                  placeholder="e.g. Holy Family Parish"
                  className="input-green"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="label-green">Occasion / Title</label>
                <input
                  name="occasion"
                  value={form.occasion}
                  onChange={handleChange}
                  placeholder="e.g. 16th Sunday OT, Wedding, Ordination..."
                  className="input-green"
                />
              </div>
              <div>
                <label className="label-green">Date</label>
                <input
                  type="date"
                  name="date"
                  value={form.date}
                  onChange={handleChange}
                  className="input-green"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="label-green">Conductor</label>
                <input
                  name="conductor"
                  value={form.conductor}
                  onChange={handleChange}
                  placeholder="Conductor's Name"
                  className="input-green"
                />
              </div>
              <div>
                <label className="label-green">Organist / Keyboardist</label>
                <input
                  name="organist"
                  value={form.organist}
                  onChange={handleChange}
                  placeholder="Organist's Name"
                  className="input-green"
                />
              </div>
            </div>
            {/* Separator */}
            <div className="w-full border-t border-green-200 my-2" />

            {/* DYNAMIC SONG FIELDS */}
            <div className="rounded-xl border border-green-100 bg-green-50 p-4 shadow-inner">
              {SONG_FIELDS.map((field) => (
                <div key={field.key} className="mb-6">
                  <div className="text-green-800 font-bold text-base mb-1">
                    {field.label}
                    {field.key === "responsorial_psalm" && (
                      <span className="text-green-500 text-xs ml-2">(Psalmist below)</span>
                    )}
                  </div>
                  <div className="flex flex-col gap-2">
                    {form[field.key].map((song, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <input
                          value={song}
                          onChange={e => handleSongChange(field.key, idx, e.target.value)}
                          className="input-green flex-1"
                          placeholder={`Enter ${field.label.toLowerCase()}`}
                          onKeyDown={e => { if (e.key === "Enter") e.preventDefault(); }}
                        />
                        {form[field.key].length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeSongRow(field.key, idx)}
                            className="px-2 py-1 rounded hover:bg-red-50 text-red-400 text-lg"
                            aria-label="Remove"
                          >×</button>
                        )}
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => addSongRow(field.key)}
                      className="text-xs bg-green-100 text-green-700 rounded px-2 py-1 shadow hover:bg-green-200 transition"
                    >
                      + Add another {field.label.split(" ")[0].toLowerCase()}
                    </button>
                  </div>
                  {field.key === "responsorial_psalm" && (
                    <input
                      name="psalmist"
                      value={form.psalmist}
                      onChange={handleChange}
                      className="input-green mt-2"
                      placeholder="Psalmist (optional)"
                      onKeyDown={e => { if (e.key === "Enter") e.preventDefault(); }}
                    />
                  )}
                </div>
              ))}
            </div>

            <div>
              <label className="label-green">Other Instrumentalists</label>
              <textarea
                name="instrumentalists"
                value={form.instrumentalists}
                onChange={handleChange}
                placeholder="E.g. Conga drummer, Maracas, Tambourine, Woodblock, Gong, Pot"
                className="input-green"
                rows={2}
              />
            </div>

            <button
              type="submit"
              className="mt-5 w-full bg-green-700 hover:bg-green-800 text-white font-extrabold rounded-2xl shadow-md py-3 px-7 text-lg transition-all active:scale-98"
            >
              🎼 Create Selection
            </button>
          </form>
        )}

        {/* PREVIEW & IMAGE SECTION */}
        {showResult && (
          <div className="w-full flex flex-col items-center">
            <div
              ref={resultRef}
              className="w-full max-w-2xl bg-white rounded-3xl shadow-2xl p-8 border border-green-200 relative animate-fade-in"
              style={{
                fontFamily: "'Inter', 'Segoe UI', 'Arial', sans-serif",
                marginBottom: 24,
              }}
            >
              {/* No watermark */}
              <div className="relative z-10">
                {/* Choir & Church */}
                <div className="text-center mb-2">
                  <div className="font-bold text-2xl text-green-900">{form.choir_name || "Choir Name"}</div>
                  <div className="font-semibold text-green-700 text-xl">{form.church_name || "Church Name"}</div>
                </div>
                <div className="flex justify-between mb-2 items-center text-green-800">
                  <div>
                    <div className="text-xl font-extrabold">{form.occasion || "Occasion/Title"}</div>
                    <div className="text-green-600 text-base font-bold">{form.date}</div>
                  </div>
                  <div className="text-right">
                    <div>
                      <span className="font-bold">Conductor:</span>{" "}
                      {form.conductor || <span className="text-gray-400 italic">---</span>}
                    </div>
                    <div>
                      <span className="font-bold">Organist:</span>{" "}
                      {form.organist || <span className="text-gray-400 italic">---</span>}
                    </div>
                  </div>
                </div>
                <hr className="border-green-100 mb-2" />
                <div className="grid gap-4">
                  {SONG_FIELDS.map(field => {
                    const items = form[field.key].filter(line => line && line.trim() !== "");
                    if (!items.length && field.key !== "responsorial_psalm") return null;
                    return (
                      <div key={field.key}>
                        <div className="font-bold text-green-800 text-base mb-1">{field.label}</div>
                        <ul className="pl-3 list-disc text-green-900 font-medium">
                          {items.map((line, idx) => (
                            <li key={idx} className="bg-green-50 rounded-xl px-3 py-1 mb-1">{line}</li>
                          ))}
                          {field.key === "responsorial_psalm" && form.psalmist && (
                            <li className="text-green-500 italic list-none">Psalmist: {form.psalmist}</li>
                          )}
                        </ul>
                      </div>
                    );
                  })}
                  {form.instrumentalists && (
                    <div>
                      <div className="font-bold text-green-800 text-base mb-1">Instrumentalists</div>
                      <div className="bg-green-50 rounded-xl px-3 py-2 text-green-900 font-medium">
                        {form.instrumentalists}
                      </div>
                    </div>
                  )}
                </div>
                <div className="mt-8 pt-6 border-t border-green-100 text-center text-xs text-green-500 opacity-80">
                  Generated with <span className="font-bold text-green-900">ChoirCenter.com Selections Manager</span>
                </div>
              </div>
            </div>
            {/* BUTTON to Save/Share as Image */}
            <button
              className="mt-2 w-full md:w-auto bg-green-600 hover:bg-green-700 text-white font-bold rounded-xl shadow-md py-3 px-7 text-lg transition-all active:scale-98"
              onClick={handleSaveShare}
              disabled={generating}
            >
              {generating ? "Generating Image..." : "Save/Share as Image"}
            </button>
            {/* Show generated image and share/download only after image is ready */}
            {imgUrl && (
              <>
                <div className="w-full flex flex-col items-center my-6">
                  <img src={imgUrl} alt="Selection Table" className="rounded-xl shadow-2xl border" style={{ maxWidth: "100%" }} />
                </div>
                <div className="flex flex-col gap-4 mb-8 items-center">
                  <div className="flex gap-4">
                    {/* WhatsApp */}
                    <button
                      className="rounded-full w-14 h-14 flex items-center justify-center shadow-md bg-green-600 hover:bg-green-700 transition group"
                      title="Share on WhatsApp"
                      onClick={shareOnWhatsApp}
                    >
                      <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                        <circle cx="16" cy="16" r="16" fill="#25D366" />
                        <path d="M22.75 19.8c-.33-.16-1.94-.96-2.23-1.07-.3-.11-.52-.16-.74.17-.22.32-.85 1.07-1.04 1.3-.19.22-.38.24-.7.08-.33-.16-1.41-.52-2.68-1.66-.99-.88-1.67-1.97-1.87-2.29-.2-.32-.02-.5.15-.66.16-.16.33-.41.5-.61.17-.2.22-.33.33-.55.11-.22.06-.41-.03-.57-.09-.16-.75-1.8-1.03-2.46-.27-.66-.54-.56-.74-.57h-.64c-.22 0-.57.08-.87.41-.3.33-1.14 1.12-1.14 2.73 0 1.6 1.16 3.15 1.32 3.37.16.22 2.3 3.51 5.55 4.62 3.26 1.11 3.12.76 3.69.71.56-.05 1.83-.75 2.09-1.48.26-.73.26-1.36.18-1.48-.08-.12-.3-.2-.62-.37z" fill="white"/>
                      </svg>
                    </button>
                    {/* Facebook */}
                    <button
                      className="rounded-full w-14 h-14 flex items-center justify-center shadow-md bg-[#1877F3] hover:bg-blue-900 transition group"
                      title="Share on Facebook"
                      onClick={shareOnFacebook}
                    >
                      <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                        <circle cx="16" cy="16" r="16" fill="#1877F3"/>
                        <path d="M20.667 16H18V24h-3.333V16h-1.334v-2.667h1.334v-1.62c0-1.857.953-2.713 2.936-2.713h2.063V12h-1.292c-.469 0-.707.233-.707.684v1.316h2l-.263 2.667z" fill="white"/>
                      </svg>
                    </button>
                  </div>
                  <div className="flex gap-12 mt-2">
                    <span className="text-green-700 text-sm font-semibold flex flex-col items-center">
                      Share to WhatsApp
                    </span>
                    <span className="text-blue-700 text-sm font-semibold flex flex-col items-center">
                      Share to Facebook
                    </span>
                  </div>
                  <button
                    onClick={downloadImage}
                    className="mt-4 bg-green-100 text-green-800 px-4 py-2 rounded-lg shadow hover:bg-green-200 font-semibold"
                  >
                    Download Image
                  </button>
                </div>
                <button
                  onClick={handleNew}
                  className="bg-green-50 text-green-900 font-semibold rounded-xl px-6 py-3 shadow hover:bg-green-100 active:scale-98 transition-all"
                >
                  🔄 Start New
                </button>
              </>
            )}
          </div>
        )}
      </div>
      {/* Tailwind helpers */}
      <style>{`
        .label-green {
          @apply block text-xs font-semibold text-green-700 mb-1 ml-1 tracking-wide uppercase;
        }
        .input-green {
          @apply w-full bg-white border border-green-200 rounded-xl py-2 px-4 focus:outline-none focus:ring-2 focus:ring-green-300 font-medium text-green-900 placeholder:text-green-300 transition-all;
        }
        @keyframes fade-in { from { opacity: 0; translate: 0 40px } to { opacity: 1; translate: 0 0 } }
        .animate-fade-in { animation: fade-in 0.5s cubic-bezier(.39,.58,.57,1.32) both; }
      `}
      </style>
    </div>
  );
}
